import { useCallback, useEffect, useRef, useState } from "react";
import { useChatSocketOptional } from "../../context/ChatSocketContext";
import { useAuth } from "../../context/AuthContext";

/**
 * Echtzeit-Andockpunkt an den Socket.IO-Raum `contact:<id>` (Kundentagebuch).
 *
 * Über EINEN Join/Leave laufen beide Frame-Familien:
 *  - `contact:notes:dirty` — metadatenarmes Signal, dass sich an den Notizen
 *    etwas geändert hat. Der Aufrufer lädt daraufhin über seinen normalen
 *    Lesepfad neu (dort greift das Kontakt-Gate).
 *  - `contact:draft` — der Entwurfstext, den gerade jemand anderes tippt.
 *    Flüchtig, nie persistiert; leerer Text = Entwurf beendet.
 *
 * WICHTIG (Leave-Falle, analog `useVorgangNotesLive`): `contact:leave` ist
 * socket-WEIT. Deshalb liegen Dirty- und Draft-Listener bewusst in EINEM Hook —
 * zwei getrennte Hooks würden sich gegenseitig aus dem Raum werfen.
 *
 * Eigene Frames werden verworfen: der Server sendet Drafts ohnehin nur an die
 * anderen, das Dirty-Signal aber an alle im Raum (inkl. Auslöser) — dort filtert
 * die `actorUserId`, damit der eigene Save keinen zweiten Reload auslöst.
 */

/** Ein fremder Entwurf, der gerade getippt wird. */
export interface FremderEntwurf {
    userId: string;
    text: string;
}

interface Options {
    /** Wird gerufen, wenn sich die Notizen im Raum geändert haben (fremd). */
    onNotesChanged: () => void | Promise<void>;
}

/**
 * Sende-Takt für den Entwurfstext. 400 ms ist der Kompromiss aus „fühlt sich
 * live an" und „flutet den Raum nicht" — bei schnellem Tippen entstehen so
 * höchstens 2–3 Frames pro Sekunde statt einem pro Anschlag.
 */
const DRAFT_THROTTLE_MS = 400;

/** Nach dieser Stille gilt ein fremder Entwurf als beendet (Sicherheitsnetz). */
const DRAFT_STALE_MS = 15_000;

export function useKundentagebuchLive(
    contactId: number | null | undefined,
    { onNotesChanged }: Options,
): {
    connected: boolean;
    fremdeEntwuerfe: FremderEntwurf[];
    sendeEntwurf: (text: string) => void;
} {
    const socket = useChatSocketOptional();
    const connected = socket?.connected ?? false;
    const { user } = useAuth();
    const currentUserId = user?.id ?? null;

    const changedRef = useRef(onNotesChanged);
    changedRef.current = onNotesChanged;

    const [entwuerfe, setEntwuerfe] = useState<Map<string, { text: string; at: number }>>(
        () => new Map(),
    );

    // Throttle-State des eigenen Sendens.
    const lastSentAt = useRef(0);
    const pendingText = useRef<string | null>(null);
    const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        if (!socket || !connected || !contactId || !Number.isFinite(contactId)) return;

        // Raum betreten — der Server prüft das Kontakt-Leserecht (fail-closed).
        // Ein abgelehnter Join wird still verworfen: dann bleibt es beim
        // manuellen Aktualisieren, die Seite funktioniert weiterhin.
        void socket.emitAck("contact:join", { contactId }).catch(() => undefined);

        const offDirty = socket.on(
            "contact:notes:dirty",
            (p: { contactId?: number; actorUserId?: string | null }) => {
                if (!p || p.contactId !== contactId) return;
                // Eigene Änderung: die Liste wurde lokal schon neu geladen.
                if (p.actorUserId && p.actorUserId === currentUserId) return;
                void changedRef.current();
            },
        );

        const offDraft = socket.on(
            "contact:draft",
            (p: { contactId?: number; userId?: string; text?: string }) => {
                if (!p || p.contactId !== contactId || !p.userId) return;
                if (p.userId === currentUserId) return;
                const text = typeof p.text === "string" ? p.text : "";
                setEntwuerfe((prev) => {
                    const next = new Map(prev);
                    if (text) next.set(p.userId!, { text, at: Date.now() });
                    else next.delete(p.userId!);
                    return next;
                });
            },
        );

        return () => {
            offDirty();
            offDraft();
            socket.emit("contact:leave", { contactId });
            setEntwuerfe(new Map());
        };
    }, [socket, connected, contactId, currentUserId]);

    // Sicherheitsnetz: verwaiste Entwürfe (Sender abgestürzt/Frame verloren)
    // nach einer Weile fallen lassen, damit kein „schreibt gerade …" klebt.
    useEffect(() => {
        if (entwuerfe.size === 0) return;
        const t = setInterval(() => {
            const cutoff = Date.now() - DRAFT_STALE_MS;
            setEntwuerfe((prev) => {
                let changed = false;
                const next = new Map(prev);
                for (const [userId, d] of prev) {
                    if (d.at < cutoff) {
                        next.delete(userId);
                        changed = true;
                    }
                }
                return changed ? next : prev;
            });
        }, 5000);
        return () => clearInterval(t);
    }, [entwuerfe.size]);

    /** Entwurfstext senden (gedrosselt); leerer Text beendet den Entwurf. */
    const sendeEntwurf = useCallback(
        (text: string) => {
            if (!socket || !connected || !contactId) return;

            const flush = () => {
                const payload = pendingText.current;
                pendingText.current = null;
                lastSentAt.current = Date.now();
                if (payload === null) return;
                socket.emit("contact:draft", { contactId, text: payload });
            };

            // Das Ende eines Entwurfs (leerer Text) geht sofort raus — sonst
            // bliebe bei den anderen kurz ein toter Entwurf stehen.
            if (!text) {
                if (timer.current) {
                    clearTimeout(timer.current);
                    timer.current = null;
                }
                pendingText.current = "";
                flush();
                return;
            }

            pendingText.current = text;
            const seit = Date.now() - lastSentAt.current;
            if (seit >= DRAFT_THROTTLE_MS) {
                if (timer.current) {
                    clearTimeout(timer.current);
                    timer.current = null;
                }
                flush();
                return;
            }
            if (!timer.current) {
                timer.current = setTimeout(() => {
                    timer.current = null;
                    flush();
                }, DRAFT_THROTTLE_MS - seit);
            }
        },
        [socket, connected, contactId],
    );

    // Beim Unmount einen offenen Entwurf widerrufen.
    useEffect(() => {
        return () => {
            if (timer.current) clearTimeout(timer.current);
            if (socket && contactId && pendingText.current !== null) {
                socket.emit("contact:draft", { contactId, text: "" });
            }
        };
    }, [socket, contactId]);

    const fremdeEntwuerfe: FremderEntwurf[] = [...entwuerfe.entries()].map(([userId, d]) => ({
        userId,
        text: d.text,
    }));

    return { connected, fremdeEntwuerfe, sendeEntwurf };
}
